// Constants for Practice Setup → Healthcare Program → Eligibility setup.
// Persisted shape matches the backend (see myriad-backend
// src/server/utility/practice/healthcare_program/lib/eligibility_setup.js).
// UI-only metadata (label, description, pricing) lives in the DEFINITIONS map
// below and is NOT persisted.

import { iaRa } from 'lib/helpers/utility';
import { DEFAULT_PAYMENT_SETUP } from 'lib/constants/healthcareProgram/paymentSetup';

export const HEALTHCARE_PROGRAM_QUERY_KEY = 'healthcare-program-config';
export const FEATURE_WAITLIST_QUERY_KEY = 'feature-waitlist';

export const ELIGIBILITY_STEP_FIELDS = {
  CHECK_270_271: 'check_270_271',
  ALTERNATE_CLEARINGHOUSE_AI: 'alternate_clearinghouse_ai',
  ALERT_BILLER: 'alert_biller',
  AGENTIC_PORTAL: 'agentic_payer_portal',
  AGENTIC_PHONE: 'agentic_payer_phone'
};

export const COMING_SOON_FIELDS = [
  ELIGIBILITY_STEP_FIELDS.AGENTIC_PORTAL,
  ELIGIBILITY_STEP_FIELDS.AGENTIC_PHONE
];

// Steps that run a real-time automated eligibility check and can return benefit
// data. `alert_biller` is a fallback layered on top of these, so it can never be
// the only enabled step (mirrors the backend rule in
// healthcare_program/lib/validators.js).
export const CLEARINGHOUSE_STEP_FIELDS = [
  ELIGIBILITY_STEP_FIELDS.CHECK_270_271,
  ELIGIBILITY_STEP_FIELDS.ALTERNATE_CLEARINGHOUSE_AI
];

export const ROUTE_TO_VALUES = {
  TEAM_MEMBER: 'team_member',
  BILLER_QUEUE: 'biller_queue'
};

export const ASSURANCE_THRESHOLD_MAX = 95;
export const ASSURANCE_THRESHOLD_MIN = 0;
export const DEFAULT_AUTO_FINALIZE_GFE = 80;

// UI-only metadata keyed by field. Merged into each step in render.
export const ELIGIBILITY_STEP_DEFINITIONS = {
  [ELIGIBILITY_STEP_FIELDS.CHECK_270_271]: {
    label: '270/271 eligibility check'
  },
  [ELIGIBILITY_STEP_FIELDS.ALTERNATE_CLEARINGHOUSE_AI]: {
    label: 'Alternate clearinghouse + Myriad AI data aggregation',
    isAi: true
  },
  [ELIGIBILITY_STEP_FIELDS.ALERT_BILLER]: {
    label: 'Alert biller / internal billing queue'
  },
  [ELIGIBILITY_STEP_FIELDS.AGENTIC_PORTAL]: {
    label: 'Agentic payer portal',
    description:
      'AI agent logs into the payer portal and retrieves real-time benefit data on your behalf.',
    pricing: '$3.50 per verification'
  },
  [ELIGIBILITY_STEP_FIELDS.AGENTIC_PHONE]: {
    label: 'Agentic payer phone call',
    description:
      'AI agent calls the payer IVR or live representative and transcribes benefit data directly into the patient record.',
    pricing: '$3.50 per call'
  }
};

export const DEFAULT_ELIGIBILITY_SETUP = {
  steps: [
    { field: ELIGIBILITY_STEP_FIELDS.CHECK_270_271, enabled: true },
    { field: ELIGIBILITY_STEP_FIELDS.ALTERNATE_CLEARINGHOUSE_AI, enabled: true },
    {
      field: ELIGIBILITY_STEP_FIELDS.ALERT_BILLER,
      enabled: false,
      config: { route_to: ROUTE_TO_VALUES.BILLER_QUEUE }
    },
    { field: ELIGIBILITY_STEP_FIELDS.AGENTIC_PORTAL, enabled: false, coming_soon: true },
    { field: ELIGIBILITY_STEP_FIELDS.AGENTIC_PHONE, enabled: false, coming_soon: true }
  ],
  assurance_thresholds: {
    auto_finalize_gfe: DEFAULT_AUTO_FINALIZE_GFE
  }
};

export const DEFAULT_HEALTHCARE_PROGRAM_CONFIG = {
  eligibility_setup: DEFAULT_ELIGIBILITY_SETUP,
  payment_program: DEFAULT_PAYMENT_SETUP,
  incomeiqueue_rcm: null,
  acknowledgments: null
};

export const getEligibilitySetupError = (setup) => {
  const steps = iaRa(setup?.steps);
  const isStepEnabled = (field) => steps.some((s) => s.field === field && s.enabled);
  const alertStep = steps.find((s) => s.field === ELIGIBILITY_STEP_FIELDS.ALERT_BILLER);

  if (alertStep?.enabled && !CLEARINGHOUSE_STEP_FIELDS.some(isStepEnabled)) {
    return {
      title: 'Enable a clearinghouse check',
      message:
        'The "Alert biller / internal billing queue" step is a fallback — turn on at least one clearinghouse check (270/271 or alternate clearinghouse) before you can save.'
    };
  }

  if (
    alertStep?.enabled &&
    alertStep?.config?.route_to === ROUTE_TO_VALUES.TEAM_MEMBER &&
    !alertStep?.config?.assignee_id
  ) {
    return {
      title: 'Select a team member to notify',
      message: 'The "Alert a team member" step needs an assignee before you can save.'
    };
  }

  return null;
};

export const isEligibilitySetupValid = (setup) => !getEligibilitySetupError(setup);

// Merges a persisted step with its UI-only definition for rendering.
export const hydrateStep = (step) => ({
  ...ELIGIBILITY_STEP_DEFINITIONS[step.field],
  ...step,
  comingSoon: !!step.coming_soon,
  routeTo: step?.config?.route_to
});
